import { SKINS, skinById } from "./game/skins";

export { SKINS };

/** Skins that older profiles unlocked by playing (before the coin shop). */
const LEGACY_RUN_UNLOCKS: Record<string, number> = {
  noob_green: 3, pro_red: 8, pro_blue: 14, pro_purple: 22, hacker_gold: 35,
};

export interface MissionState {
  id: string;
  label: string;
  target: number;
  progress: number;
  reward: number;
  claimed: boolean;
}

export interface Profile {
  version: 3;
  coins: number;
  bestSolo: number;
  bestArena: number;
  runs: number;
  eaten: number;
  equippedSkin: string;
  /** Skin ids bought in the shop (the free default is always owned). */
  ownedSkins?: string[];
  missions: MissionState[];
  /** Mission set number; a fresh, harder set unlocks once all are claimed. */
  missionLevel?: number;
}

export function missionSet(level: number): MissionState[] {
  const k = level + 1;
  return [
    { id: `eat_${level}`, label: "", target: 30 * k, progress: 0, reward: 75 * k, claimed: false },
    { id: `score_${level}`, label: "", target: 2500 * k, progress: 0, reward: 120 * k, claimed: false },
    { id: `play_${level}`, label: "", target: 5, progress: 0, reward: 100 + 25 * level, claimed: false },
    { id: `win_${level}`, label: "", target: 1 + level, progress: 0, reward: 150 * k, claimed: false },
  ];
}

/** Mission kind from its id ("eat_3" -> "eat"). */
export function missionKind(id: string) {
  return id.split("_")[0] as "eat" | "score" | "play" | "win";
}

const STORAGE_KEY = "dunkles-loch-profile-v3";

export function newProfile(): Profile {
  return {
    version: 3,
    coins: 0,
    bestSolo: 0,
    bestArena: 0,
    runs: 0,
    eaten: 0,
    equippedSkin: "noob_black",
    ownedSkins: ["noob_black"],
    missions: missionSet(0),
    missionLevel: 0,
  };
}

export function loadProfile(): Profile {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "") as Partial<Profile>;
    if (parsed.version === 3) return migrateSkins({ ...newProfile(), ...parsed } as Profile);
  } catch {
    // A corrupt profile intentionally falls back to a clean v3 profile.
  }
  return newProfile();
}

export function saveProfile(profile: Profile) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export function recordRun(profile: Profile, mode: "solo" | "arena", score: number, eaten: number, arenaCoins = 0, rank?: number): Profile {
  const coins = arenaCoins || Math.floor(score / 100);
  const next: Profile = {
    ...profile,
    coins: profile.coins + coins,
    runs: profile.runs + 1,
    eaten: profile.eaten + eaten,
    bestSolo: mode === "solo" ? Math.max(profile.bestSolo, score) : profile.bestSolo,
    bestArena: mode === "arena" ? Math.max(profile.bestArena, score) : profile.bestArena,
    missions: profile.missions.map((mission) => {
      const kind = missionKind(mission.id);
      if (kind === "eat") return { ...mission, progress: Math.min(mission.target, mission.progress + eaten) };
      if (kind === "score") return { ...mission, progress: Math.max(mission.progress, Math.min(mission.target, score)) };
      if (kind === "play") return { ...mission, progress: Math.min(mission.target, mission.progress + 1) };
      if (kind === "win" && rank === 1) return { ...mission, progress: Math.min(mission.target, mission.progress + 1) };
      return mission;
    }),
  };
  saveProfile(next);
  return next;
}

export function claimMission(profile: Profile, id: string): Profile {
  let reward = 0;
  const missions = profile.missions.map((mission) => {
    if (mission.id !== id || mission.claimed || mission.progress < mission.target) return mission;
    reward = mission.reward;
    return { ...mission, claimed: true };
  });
  let next: Profile = { ...profile, coins: profile.coins + reward, missions };
  if (missions.every((mission) => mission.claimed)) {
    const level = (profile.missionLevel ?? 0) + 1;
    next = { ...next, missionLevel: level, missions: missionSet(level) };
  }
  saveProfile(next);
  return next;
}

/** Older profiles keep every skin their run count had unlocked. */
export function migrateSkins(profile: Profile): Profile {
  if (profile.ownedSkins && profile.ownedSkins.length) return profile;
  const owned = ["noob_black", ...Object.entries(LEGACY_RUN_UNLOCKS).filter(([, runs]) => profile.runs >= runs).map(([id]) => id)];
  if (!owned.includes(profile.equippedSkin)) owned.push(profile.equippedSkin);
  return { ...profile, ownedSkins: owned };
}

export function ownsSkin(profile: Profile, id: string) {
  const skin = skinById(id);
  return skin.price === 0 || (profile.ownedSkins ?? []).includes(id);
}

/** Buys a skin with coins; returns the unchanged profile when not affordable. */
export function buySkin(profile: Profile, id: string): Profile {
  const skin = skinById(id);
  if (skin.id !== id || ownsSkin(profile, id) || profile.coins < skin.price) return profile;
  const next: Profile = {
    ...profile,
    coins: profile.coins - skin.price,
    ownedSkins: [...(profile.ownedSkins ?? ["noob_black"]), id],
    equippedSkin: id,
  };
  saveProfile(next);
  return next;
}

export function equipSkin(profile: Profile, id: string): Profile {
  if (!ownsSkin(profile, id)) return profile;
  const next = { ...profile, equippedSkin: id };
  saveProfile(next);
  return next;
}
